import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useContext,
  ReactNode,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Pagination, Modal } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../providers/GlobalProvider';
import {
  ExportHubAPI,
  OverviewProduct,
  OverviewFacetOptions,
  OverviewKpis,
  SavedView,
} from 'api/ExportHubAPI';
import { JurisdictionPill, StatusPill, TierBadge } from './productPresets';
import { KpiTile, KpiGroup } from './kpiTiles';
import ProductFilterBar from './ProductFilterBar';
import AddProductModal from './AddProductModal';
import StyledSingleSelect from './StyledSingleSelect';
import SendPackageDialog from './SendPackageDialog';
import {
  ProductFilters,
  EMPTY_FILTERS,
  DEFAULT_COLUMNS,
  SavedViewState,
  parseFilters,
  applyFiltersToParams,
  parseColumns,
  applyColumnsToParams,
  filtersToApiParams,
  countActiveFilters,
} from './productFilters';

const PAGE_SIZE = 25;
const SCOPE = 'overview';

interface ColumnRender {
  label: string;
  sortKey?: string;
  render: (_p: OverviewProduct) => ReactNode;
}

const COLUMN_RENDER: Record<string, ColumnRender> = {
  name: {
    label: 'Product',
    sortKey: 'name',
    render: (p) => (
      <div className='eh-product-name-cell'>
        <span className='eh-product-name'>{p.name}</span>
        <span className='eh-product-sub'>
          {p.internal_sku ? `SKU ${p.internal_sku}` : 'No SKU'}
          <TierBadge value={p.tier} />
        </span>
      </div>
    ),
  },
  hts_code: { label: 'HTS Code', sortKey: 'hts_code', render: (p) => p.hts_code || '—' },
  country_of_origin: {
    label: 'Origin',
    sortKey: 'country_of_origin',
    render: (p) => p.country_of_origin || '—',
  },
  compliance_status: {
    label: 'Compliance',
    sortKey: 'compliance_status',
    render: (p) => <StatusPill value={p.compliance_status} />,
  },
  jurisdiction: {
    label: 'Jurisdiction',
    sortKey: 'jurisdiction',
    render: (p) => <JurisdictionPill value={p.jurisdiction} />,
  },
  manufacturer: { label: 'Manufacturer', render: (p) => p.manufacturer || '—' },
  destinations: {
    label: 'Destinations',
    render: (p) =>
      p.intended_destination_countries.length
        ? p.intended_destination_countries.join(', ')
        : '—',
  },
  licenses: { label: 'Licenses', render: (p) => p.active_license_count },
  last_screened_at: {
    label: 'Last Screened',
    sortKey: 'last_screened_at',
    render: (p) =>
      p.last_screened_at ? moment(p.last_screened_at).format('MM/DD/YYYY') : '—',
  },
  last_updated: {
    label: 'Updated',
    sortKey: 'last_updated',
    render: (p) => moment(p.last_updated).format('MM/DD/YYYY'),
  },
};

const csvCell = (v: unknown): string => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const ProductsOverview: React.FC = () => {
  const { cur_user } = useContext(GlobalContext);
  const location = useLocation();
  const history = useHistory();
  const groupId = cur_user?.selected_team?.id;

  const filters = useMemo(
    () => parseFilters(new URLSearchParams(location.search)),
    [location.search],
  );
  const visibleColumns = useMemo(
    () => parseColumns(new URLSearchParams(location.search)),
    [location.search],
  );
  const activeViewId = useMemo(
    () => new URLSearchParams(location.search).get('view'),
    [location.search],
  );

  const [products, setProducts] = useState<OverviewProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const [facets, setFacets] = useState<OverviewFacetOptions>({
    origins: [],
    destinations: [],
    htsChapters: [],
    databases: [],
    total: 0,
  });
  const [kpis, setKpis] = useState<OverviewKpis | null>(null);
  const [savedViews, setSavedViews] = useState<SavedView[]>([]);
  const [savingView, setSavingView] = useState(false);
  const [dbPickerOpen, setDbPickerOpen] = useState(false);
  const [addDbId, setAddDbId] = useState<string>('');
  const [addOpen, setAddOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);

  const filtersKey = JSON.stringify(filters);

  const updateUrl = useCallback(
    (mutate: (_sp: URLSearchParams) => void) => {
      const sp = new URLSearchParams(location.search);
      mutate(sp);
      history.replace({ pathname: location.pathname, search: sp.toString() });
    },
    [location.search, location.pathname, history],
  );

  const setFilters = (next: ProductFilters): void =>
    updateUrl((sp) => {
      applyFiltersToParams(sp, next);
      sp.delete('view');
    });
  const setColumns = (next: string[]): void =>
    updateUrl((sp) => {
      applyColumnsToParams(sp, next);
      sp.delete('view');
    });
  const applyView = (v: SavedView): void => {
    const state = (v.facets || {}) as Partial<SavedViewState>;
    updateUrl((sp) => {
      applyFiltersToParams(sp, { ...EMPTY_FILTERS, ...(state.filters || {}) });
      applyColumnsToParams(
        sp,
        state.columns && state.columns.length ? state.columns : DEFAULT_COLUMNS,
      );
      sp.set('view', v.id);
    });
  };

  useEffect(() => {
    setPage(1);
  }, [filtersKey]);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    if (!groupId) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await ExportHubAPI.getOverviewProducts(groupId, {
        page: page,
        limit: PAGE_SIZE,
        search: debouncedSearch || undefined,
        sortBy: sortBy,
        sortOrder: sortOrder,
        ...filtersToApiParams(filters),
      });
      setProducts(res.products);
      setTotalPages(res.pagination.totalPages || 1);
      setTotalItems(res.pagination.totalItems || 0);
    } catch (err) {
      console.error('Failed to load overview products:', err);
      enqueueSnackbar('Failed to load products.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [groupId, page, debouncedSearch, sortBy, sortOrder, filtersKey]);

  useEffect(() => {
    load();
  }, [load]);

  const refreshFacets = useCallback(async () => {
    if (!groupId) return;
    try {
      setFacets(await ExportHubAPI.getOverviewFacets(groupId));
    } catch (err) {
      console.error('Failed to load overview facets:', err);
    }
  }, [groupId]);

  const refreshKpis = useCallback(async () => {
    if (!groupId) return;
    try {
      setKpis(await ExportHubAPI.getOverviewKpis(groupId));
    } catch (err) {
      console.error('Failed to load overview KPIs:', err);
    }
  }, [groupId]);

  const refreshViews = useCallback(async () => {
    if (!groupId) return;
    try {
      setSavedViews(await ExportHubAPI.getSavedViews(groupId, SCOPE));
    } catch (err) {
      console.error('Failed to load saved views:', err);
    }
  }, [groupId]);

  useEffect(() => {
    refreshFacets();
    refreshKpis();
    refreshViews();
  }, [refreshFacets, refreshKpis, refreshViews]);

  const handleSaveView = async (name: string): Promise<void> => {
    if (!groupId) return;
    setSavingView(true);
    try {
      const view = await ExportHubAPI.createSavedView({
        scope: SCOPE,
        name: name,
        facets: { filters: filters, columns: visibleColumns },
        group_id: groupId,
      });
      await refreshViews();
      updateUrl((sp) => sp.set('view', view.id));
      enqueueSnackbar('View saved.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to save view.', { variant: 'error' });
    } finally {
      setSavingView(false);
    }
  };
  const handlePinView = async (v: SavedView): Promise<void> => {
    if (!groupId) return;
    try {
      await ExportHubAPI.updateSavedView(v.id, groupId, { is_pinned: !v.is_pinned });
      await refreshViews();
    } catch (err) {
      enqueueSnackbar('Failed to update view.', { variant: 'error' });
    }
  };
  const handleRenameView = async (v: SavedView, name: string): Promise<void> => {
    if (!groupId || !name.trim()) return;
    try {
      await ExportHubAPI.updateSavedView(v.id, groupId, { name: name.trim() });
      await refreshViews();
    } catch (err) {
      enqueueSnackbar('Failed to rename view.', { variant: 'error' });
    }
  };
  const handleDeleteView = async (v: SavedView): Promise<void> => {
    if (!groupId) return;
    try {
      await ExportHubAPI.deleteSavedView(v.id, groupId);
      await refreshViews();
      if (activeViewId === v.id) updateUrl((sp) => sp.delete('view'));
      enqueueSnackbar('View deleted.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar('Failed to delete view.', { variant: 'error' });
    }
  };

  const handleSort = (sortKey: string): void => {
    if (sortBy === sortKey) setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    else {
      setSortBy(sortKey);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const exportCsv = async (): Promise<void> => {
    if (!groupId) return;
    try {
      const res = await ExportHubAPI.getOverviewProducts(groupId, {
        page: 1,
        limit: 200,
        search: debouncedSearch || undefined,
        sortBy: sortBy,
        sortOrder: sortOrder,
        ...filtersToApiParams(filters),
      });
      const headers = [
        'Name',
        'SKU',
        'Database',
        'HTS',
        'Origin',
        'Compliance',
        'Jurisdiction',
        'Tier',
        'Active Licenses',
        'Destinations',
      ];
      const lines = res.products.map((p) =>
        [
          p.name,
          p.internal_sku,
          p.database_name,
          p.hts_code,
          p.country_of_origin,
          p.compliance_status,
          p.jurisdiction,
          p.tier,
          p.active_license_count,
          (p.intended_destination_countries || []).join('; '),
        ]
          .map(csvCell)
          .join(','),
      );
      const csv = [headers.join(','), ...lines].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products-overview-${moment().format('YYYYMMDD')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      enqueueSnackbar('Export failed.', { variant: 'error' });
    }
  };

  const openProduct = (p: OverviewProduct): void =>
    history.push(`/export-hub/databases/${p.database_id}/products/${p.id}`);

  // Columns: always show Database as the 2nd column on the cross-db overview.
  const dataColumns = visibleColumns.filter((c) => c !== 'name');
  const activeCount = countActiveFilters(filters);

  return (
    <>
      <div className='export-hub-section-header'>
        <div>
          <h2 className='export-hub-section-title'>Products Overview</h2>
          <p className='export-hub-section-sub'>
            Every product across all of your databases, in one filterable table.
          </p>
        </div>
        <div className='export-hub-header-actions'>
          <button
            className='btn-outline'
            type='button'
            onClick={exportCsv}
            disabled={totalItems === 0}
          >
            <FileDownloadOutlinedIcon style={{ fontSize: 18 }} /> Export
          </button>
          <button
            className='btn-outline'
            type='button'
            onClick={() => setSendOpen(true)}
          >
            <SendOutlinedIcon style={{ fontSize: 18 }} /> Send Package
          </button>
          <button
            className='btn-primary'
            type='button'
            onClick={() => {
              setAddDbId(facets.databases[0]?.id || '');
              setDbPickerOpen(true);
            }}
          >
            <AddIcon style={{ fontSize: 18 }} /> Add Product
          </button>
        </div>
      </div>

      {kpis && (
        <div className='eh-kpi-groups eh-kpi-overview'>
          <KpiGroup>
            <KpiTile label='Total Products' value={kpis.total} />
            <KpiTile label='Compliant' value={kpis.compliant} tone='success' />
            <KpiTile
              label='Action Required'
              value={kpis.action_required}
              tone='warning'
            />
            <KpiTile label='Restricted' value={kpis.restricted} tone='danger' />
            <KpiTile label='Unscreened' value={kpis.unscreened} tone='muted' />
            <KpiTile
              label='Recent Screenings (7d)'
              value={kpis.recent_screenings_7d}
              tone='info'
            />
          </KpiGroup>
        </div>
      )}

      <ProductFilterBar
        filters={filters}
        onFiltersChange={setFilters}
        facetOptions={facets}
        databaseOptions={facets.databases}
        visibleColumns={visibleColumns}
        onColumnsChange={setColumns}
        totalCount={facets.total}
        filteredCount={totalItems}
        savedViews={savedViews}
        activeViewId={activeViewId}
        onApplyView={applyView}
        onSaveView={handleSaveView}
        onPinView={handlePinView}
        onRenameView={handleRenameView}
        onDeleteView={handleDeleteView}
        savingView={savingView}
      />

      <div className='export-hub-panel'>
        <div className='eh-products-toolbar'>
          <div className='data-search-wrapper'>
            <SearchIcon className='data-search-icon' style={{ fontSize: 18 }} />
            <input
              className='data-search-input'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Search products by name, SKU, or HTS'
            />
          </div>
        </div>

        {loading ? (
          <div className='eh-table-skeleton'>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className='eh-skel eh-skel-row' />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className='export-hub-empty'>
            <div className='export-hub-empty-title'>
              {facets.databases.length === 0
                ? 'No databases yet'
                : 'No products found'}
            </div>
            <div className='export-hub-empty-sub'>
              {facets.databases.length === 0
                ? 'Create a database first — products are added inside a database.'
                : activeCount > 0
                ? 'No products match the current filters.'
                : 'Add products to a database to see them here.'}
            </div>
            {facets.databases.length === 0 && (
              <button
                className='btn-primary'
                type='button'
                style={{ marginTop: 16 }}
                onClick={() => history.push('/export-hub/databases')}
              >
                <AddIcon style={{ fontSize: 18 }} /> Create Database
              </button>
            )}
          </div>
        ) : (
          <table className='runs-table'>
            <thead>
              <tr>
                {['name', ...dataColumns].map((key) => {
                  const col = COLUMN_RENDER[key];
                  if (!col) return null;
                  const sortable = !!col.sortKey;
                  return (
                    <th
                      key={key}
                      className={sortable ? 'sortable' : ''}
                      onClick={() => sortable && handleSort(col.sortKey as string)}
                    >
                      {col.label}
                      {sortable && sortBy === col.sortKey && (
                        <span className='eh-sort-arrow'>
                          {sortOrder === 'asc' ? (
                            <ArrowUpwardIcon style={{ fontSize: 13 }} />
                          ) : (
                            <ArrowDownwardIcon style={{ fontSize: 13 }} />
                          )}
                        </span>
                      )}
                    </th>
                  );
                })}
                <th>Database</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr
                  key={p.id}
                  className='hoverable-row'
                  onClick={() => openProduct(p)}
                >
                  {['name', ...dataColumns].map((key) => {
                    const col = COLUMN_RENDER[key];
                    if (!col) return null;
                    return <td key={key}>{col.render(p)}</td>;
                  })}
                  <td>
                    <span className='eh-db-chip'>{p.database_name || '—'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className='hb-pagination'>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_e: React.ChangeEvent<unknown>, v: number) =>
                setPage(v)
              }
              shape='rounded'
            />
          </div>
        )}
      </div>

      {/* Add Product — pick a database first (unscoped overview, spec §5.2). */}
      <Modal
        open={dbPickerOpen}
        onClose={() => setDbPickerOpen(false)}
        className='eh-modal-root'
      >
        <div className='eh-modal eh-modal-sm' role='dialog' aria-label='Choose database'>
          <div className='eh-modal-head'>
            <h2 className='eh-modal-title'>Add Product</h2>
            <button
              className='eh-modal-close'
              onClick={() => setDbPickerOpen(false)}
              aria-label='Close'
              type='button'
            >
              <CloseIcon style={{ fontSize: 20 }} />
            </button>
          </div>
          <div className='eh-modal-body'>
            {facets.databases.length === 0 ? (
              <div className='export-hub-empty' style={{ padding: '24px 8px' }}>
                <div className='export-hub-empty-title'>No databases yet</div>
                <div className='export-hub-empty-sub'>
                  Products are added inside a database. Create one first, then
                  add products to it.
                </div>
              </div>
            ) : (
              <div className='eh-field'>
                <span className='eh-field-label'>Database</span>
                <StyledSingleSelect
                  options={facets.databases.map((d) => ({
                    value: d.id,
                    label: d.name,
                  }))}
                  value={addDbId}
                  onChange={setAddDbId}
                  inline
                />
                <span className='eh-field-hint'>
                  New products are scoped to a database.
                </span>
              </div>
            )}
          </div>
          <div className='eh-modal-foot'>
            <button
              className='btn-outline'
              type='button'
              onClick={() => setDbPickerOpen(false)}
            >
              Cancel
            </button>
            {facets.databases.length === 0 ? (
              <button
                className='btn-primary'
                type='button'
                onClick={() => {
                  setDbPickerOpen(false);
                  history.push('/export-hub/databases');
                }}
              >
                Create Database
              </button>
            ) : (
            <button
              className='btn-primary'
              type='button'
              onClick={() => {
                setDbPickerOpen(false);
                setAddOpen(true);
              }}
              disabled={!addDbId}
              style={{ opacity: addDbId ? 1 : 0.6 }}
            >
              Continue
            </button>
            )}
          </div>
        </div>
      </Modal>

      {addDbId && (
        <AddProductModal
          open={addOpen}
          databaseId={addDbId}
          onClose={() => setAddOpen(false)}
          onCreated={() => {
            setAddOpen(false);
            load();
            refreshFacets();
            refreshKpis();
          }}
        />
      )}

      <SendPackageDialog
        open={sendOpen}
        subjectLabel='filtered products'
        onClose={() => setSendOpen(false)}
      />
    </>
  );
};

export default ProductsOverview;
